import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Table, Card, Row, Col, ProgressBar } from "react-bootstrap";
import Api from "../Config/ApiConfig";
import { toast } from "react-toastify";
import moment from "moment";
import "../styles/VerificationModal.css";

const VerificationModal = ({ show, handleClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [newMaxAttempts, setNewMaxAttempts] = useState(3);
  const [updating, setUpdating] = useState(false);

  const fetchVerificationDetails = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Fetching verification details for user:", user._id);
      const response = await Api.get(`/user/${user._id}/verification`);
      console.log("Verification API response:", response);
      if (response.data.status_code === 200) {
        setVerificationData(response.data.data);
        setNewMaxAttempts(response.data.data.max_attempts);
      } else {
        console.error("API returned error:", response.data);
        toast.error(response.data.message || "Failed to fetch verification details");
      }
    } catch (error) {
      console.error("Error fetching verification details:", error);
      console.error("Error response:", error.response);
      toast.error(error.response?.data?.message || "Failed to fetch verification details");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (show && user) {
      fetchVerificationDetails();
    }
  }, [show, user, fetchVerificationDetails]);

  const handleUpdateMaxAttempts = async () => {
    if (newMaxAttempts < 3 || newMaxAttempts > 6) {
      toast.error("Max attempts must be between 3 and 6");
      return;
    }

    setUpdating(true);
    try {
      const response = await Api.put(`/user/${user._id}/verification/max-attempts`, {
        maxAttempts: newMaxAttempts
      });
      
      if (response.data.status_code === 200) {
        toast.success("Max attempts updated successfully");
        fetchVerificationDetails(); // Refresh data
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating max attempts:", error);
      toast.error(error.response?.data?.message || "Failed to update max attempts");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { variant: "success", text: "Approved", className: "status-badge-approved" },
      rejected: { variant: "danger", text: "Rejected", className: "status-badge-rejected" },
      pending: { variant: "warning", text: "Pending", className: "status-badge-pending" },
      expired: { variant: "secondary", text: "Expired", className: "status-badge-expired" }
    };
    
    const config = statusConfig[status] || { variant: "secondary", text: status, className: "status-badge-default" };
    return <span className={`modern-status-badge ${config.className}`}>{config.text}</span>;
  };

  const formatDate = (date) => {
    return date ? moment(date).format("DD MMM YYYY, h:mm A") : "N/A";
  };

  const getProgressPercentage = () => {
    if (!verificationData) return 0;
    return Math.min((verificationData.current_attempts / verificationData.max_attempts) * 100, 100);
  };

  const getProgressVariant = () => {
    const percentage = getProgressPercentage();
    if (percentage < 50) return "success";
    if (percentage < 80) return "warning";
    return "danger";
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" className="modern-verification-modal" centered>
      <Modal.Header className="modern-modal-header">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <div className="verification-icon-wrapper">
              <i className="fas fa-shield-check"></i>
            </div>
            <div>
              <h5 className="mb-0 text-white">Verification Management</h5>
              <small className="header-subtitle">Manage user verification settings and history</small>
            </div>
          </div>
          <button type="button" className="modern-close-btn" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </Modal.Header>
      
      <Modal.Body className="modern-modal-body">
        {loading ? (
          <div className="modern-loading-container">
            <div className="modern-spinner">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
            <p className="loading-text">Loading verification details...</p>
          </div>
        ) : verificationData ? (
          <div className="verification-content">
            {/* Compact User Info & Stats Row */}
            <Row className="mb-3">
              <Col md={5}>
                <Card className="modern-user-card h-100">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="user-avatar-compact">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="user-info-compact">
                        <h6 className="user-name-compact mb-1">{verificationData.user.name}</h6>
                        <p className="user-email-compact mb-1">{verificationData.user.email}</p>
                        <div className="verification-status-compact">
                          {verificationData.user.shufti_verification ? (
                            <span className="status-compact verified">
                              <i className="fas fa-check-circle"></i> Verified
                            </span>
                          ) : (
                            <span className="status-compact not-verified">
                              <i className="fas fa-times-circle"></i> Not Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={7}>
                <Card className="modern-stats-card h-100">
                  <Card.Body className="p-3">
                    <h6 className="card-title-compact mb-2">
                      <i className="fas fa-chart-line me-2"></i>Sessions Overview
                    </h6>
                    <Row className="g-2">
                      <Col xs={4}>
                        <div className="stat-item-compact">
                          <div className="stat-value-compact current-attempts">{verificationData.current_attempts}</div>
                          <div className="stat-label-compact">Sessions</div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="stat-item-compact">
                          <div className="stat-value-compact max-attempts">{verificationData.max_attempts}</div>
                          <div className="stat-label-compact">Max Sessions</div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="stat-item-compact">
                          <div className={`stat-value-compact ${verificationData.can_increase_attempts ? 'can-increase' : 'cannot-increase'}`}>
                            {verificationData.can_increase_attempts ? 'Yes' : 'No'}
                          </div>
                          <div className="stat-label-compact">Can Increase</div>
                        </div>
                      </Col>
                    </Row>
                    <div className="progress-section-compact mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="progress-label-compact">Session Progress</span>
                        <span className="progress-percentage-compact">{Math.round(getProgressPercentage())}%</span>
                      </div>
                      <ProgressBar 
                        now={getProgressPercentage()} 
                        variant={getProgressVariant()}
                        className="modern-progress-bar-compact"
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Update Max Attempts - Compact */}
            {verificationData.can_increase_attempts && (
              <Card className="modern-update-card mb-3">
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md={3}>
                      <h6 className="card-title-compact mb-0">
                        <i className="fas fa-cog me-2"></i>Update Max Sessions
                      </h6>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-0">
                        <Form.Label className="modern-form-label-compact">New Max Sessions (3-6)</Form.Label>
                        <Form.Control
                          type="number"
                          min="3"
                          max="6"
                          value={newMaxAttempts}
                          onChange={(e) => setNewMaxAttempts(parseInt(e.target.value))}
                          className="modern-form-control-compact"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Button
                        className="modern-update-btn-compact"
                        size="sm"
                        onClick={handleUpdateMaxAttempts}
                        disabled={updating || newMaxAttempts === verificationData.max_attempts}
                      >
                        {updating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1"></i>
                            Update
                          </>
                        )}
                      </Button>
                    </Col>
                    <Col md={2}>
                      <small className="text-muted">Current: {verificationData.max_attempts} sessions</small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Verification History - Compact */}
            <Card className="modern-history-card mb-3">
              <Card.Body className="p-3">
                <h6 className="card-title-compact mb-2">
                  <i className="fas fa-history me-2"></i>Verification History
                </h6>
                {verificationData.verification_history.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="modern-verification-table-compact mb-0">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Status</th>
                          <th>Attempts</th>
                          <th>Created</th>
                          <th>Verified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verificationData.verification_history.map((attempt, index) => (
                          <tr key={index} className={index === 0 ? 'latest-attempt' : ''}>
                            <td>
                              <div className="reference-cell-compact">
                                <code className="reference-code-compact">{attempt.reference.split('_').pop()}</code>
                                {index === 0 && <span className="latest-badge-compact">Latest</span>}
                              </div>
                            </td>
                            <td>{getStatusBadge(attempt.status)}</td>
                            <td>
                              <div className="attempts-cell-compact">
                                <span className="attempts-fraction-compact">{attempt.tries}/{attempt.max_tries}</span>
                                <div className="attempts-progress-compact">
                                  <div 
                                    className="attempts-progress-bar-compact" 
                                    style={{ width: `${(attempt.tries / attempt.max_tries) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="date-cell-compact">
                                <div className="date-primary-compact">{formatDate(attempt.createdAt)}</div>
                              </div>
                            </td>
                            <td>
                              <div className="date-cell-compact">
                                <div className="date-primary-compact">{formatDate(attempt.verified_at)}</div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="empty-state-compact">
                    <div className="empty-state-icon-compact">
                      <i className="fas fa-inbox"></i>
                    </div>
                    <div>
                      <h6 className="mb-1">No verification attempts found</h6>
                      <p className="text-muted mb-0">This user hasn't initiated any verification attempts yet.</p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Latest Verification Details - Compact */}
            {verificationData.latest_verification && (
              <Card className="modern-latest-card">
                <Card.Body className="p-3">
                  <h6 className="card-title-compact mb-2">
                    <i className="fas fa-clock me-2"></i>Latest Verification Details
                  </h6>
                  <Row className="g-2">
                    <Col md={4}>
                      <div className="detail-item-compact">
                        <span className="detail-label-compact">Status:</span>
                        <span className="detail-value-compact">{getStatusBadge(verificationData.latest_verification.status)}</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="detail-item-compact">
                        <span className="detail-label-compact">Expires:</span>
                        <span className="detail-value-compact">{formatDate(verificationData.latest_verification.expires_at)}</span>
                      </div>
                    </Col>
                    {verificationData.latest_verification.rejection_reason && (
                      <Col md={4}>
                        <div className="detail-item-compact">
                          <span className="detail-label-compact">Rejection:</span>
                          <span className="detail-value-compact rejection-reason-compact">{verificationData.latest_verification.rejection_reason}</span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>
        ) : (
          <div className="empty-state-compact">
            <div className="empty-state-icon-compact">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div>
              <h6 className="mb-1">No verification data available</h6>
              <p className="text-muted mb-0">Unable to load verification data for this user.</p>
            </div>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer className="modern-modal-footer">
        <Button variant="secondary" onClick={handleClose} className="modern-close-button">
          <i className="fas fa-times me-2"></i>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VerificationModal;
